import {
  DragIcon,
  ExpandIcon,
  LessonIcon,
  RoleplayIcon,
  lessonIconForType,
} from "../../assets/composer-editor-icons";
import Tooltip from "@mui/material/Tooltip";

/**
 * `mirrorChrome` renders invisible drag / item icon columns so a child row's label aligns with
 * the parent row ("Details" under unit / lesson / step / etc.).
 */
const ContentEditorTreeNode = ({
  label,
  level,
  expanded,
  onToggle,
  active,
  onClick,
  onMouseEnter,
  onFocus,
  draggable,
  showLessonIcon,
  /** STANDARD | PRACTICE | REFLECTION — picks which lesson icon `showLessonIcon` draws. */
  lessonType,
  showRoleplayIcon,
  customIcon,
  bold,
  hasChildren,
  customPaddingLeft,
  dragHandleProps,
  mirrorChrome,
  /** Icons/menus placed after the label, before the expand chevron (e.g. +, import). */
  trailingActions,
  /** Read-only per-language activation status pill shown at the end of the label strip. */
  activationBadge,
}) => {
  const paddingLeft = customPaddingLeft !== undefined ? customPaddingLeft : 14 + level * 12;
  const mainClassName = `pd-tree-node-main${active ? " pd-tree-node-main--active" : ""}${
    onClick ? " pd-tree-node-main--interactive" : ""
  }`;
  const primaryClassName = `pd-tree-node-primary${onClick ? " pd-tree-node-primary--interactive" : ""}`;

  const mirrorDrag =
    Boolean(mirrorChrome) &&
    (mirrorChrome === "drag" || mirrorChrome === "dragLesson") &&
    !draggable;
  const mirrorLesson =
    Boolean(mirrorChrome) &&
    (mirrorChrome === "lesson" || mirrorChrome === "dragLesson") &&
    !showLessonIcon;
  const TypedLessonIcon = lessonIconForType(lessonType);

  return (
    <div
      className={`pd-tree-node${active ? " active" : ""}`}
      style={{ paddingLeft, '--pd-indent': `${paddingLeft}px` }}
      role="treeitem"
      aria-selected={Boolean(active)}
      aria-expanded={hasChildren ? expanded : undefined}
    >
      {draggable && (
        <span className="pd-tree-node-drag" aria-hidden="true" {...(dragHandleProps || {})}>
          <DragIcon />
        </span>
      )}
      {mirrorDrag && (
        <span className="pd-tree-node-drag pd-tree-node-mirror" aria-hidden="true">
          <DragIcon />
        </span>
      )}
      <div className={primaryClassName}>
        {showLessonIcon && (
          <span className="pd-tree-node-item-icon" aria-hidden="true">
            <TypedLessonIcon fontSize="small" />
          </span>
        )}
        {showRoleplayIcon && (
          <span className="pd-tree-node-item-icon" aria-hidden="true">
            <RoleplayIcon fontSize="small" />
          </span>
        )}
        {customIcon && (
          <span className="pd-tree-node-item-icon" aria-hidden="true">
            {customIcon}
          </span>
        )}
        {mirrorLesson && (
          <span className="pd-tree-node-item-icon pd-tree-node-mirror" aria-hidden="true">
            <LessonIcon fontSize="small" />
          </span>
        )}
        {onClick ? (
          <button
            type="button"
            className={mainClassName}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onFocus={onFocus}
          >
            <span className={`pd-tree-node-label${bold ? " bold" : ""}`}>{label}</span>
          </button>
        ) : (
          <div className={mainClassName} onMouseEnter={onMouseEnter} onFocus={onFocus}>
            <span className={`pd-tree-node-label${bold ? " bold" : ""}`}>{label}</span>
          </div>
        )}
        {activationBadge || null}
      </div>
      {(trailingActions || hasChildren) ? (
        <div className="pd-tree-node-trail">
          {trailingActions}
          {hasChildren ? (
            <Tooltip
              title={expanded ? "Collapse" : "Expand"}
              placement="top"
              arrow
              enterDelay={400}
              disableInteractive
            >
              <button
                type="button"
                className={`pd-tree-node-chevron ${expanded ? "expanded" : "collapsed"}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggle?.();
                }}
                aria-label={expanded ? "Collapse" : "Expand"}
              >
                <ExpandIcon fontSize="small" />
              </button>
            </Tooltip>
          ) : trailingActions ? (
            <span className="pd-tree-node-chevron-spacer" aria-hidden="true" />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ContentEditorTreeNode;
